import React, { useState, useEffect, useRef } from 'react';
import { Lock } from 'lucide-react';
import { useParams, useLocation } from 'react-router-dom';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import CardDisplay from '../components/CardDisplay.js';
import { normalizeThemeVariantClient, applyThemeCssVars } from '../utils/themeUtils.js';
import type { CardData, AppSettings } from '../utils/cardUtils.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type ViewState = 'loading' | 'public-loading' | 'public-card' | 'locked' | '404' | string;

export interface PublicCardPageProps {
  /** Current view state passed from parent router/state */
  view?: ViewState;
  isPublicLoading?: boolean;
  error?: string | null;
  data?: CardData | null;
  settings?: AppSettings;
  darkMode?: boolean;
  toggleDarkMode?: () => void;
  showAlert?: (message: string, type?: string, title?: string) => void;
  /** If provided, component manages its own fetch (self-contained mode) */
  fetchCardByOrgAndSlug?: (org: string, slug: string) => void;
  fetchCardByShortCode?: (code: string) => void;
  fetchPublicCard?: (slug: string) => void;
}

// ---------------------------------------------------------------------------
// Self-contained fetch mode (when no fetch callbacks are provided)
// ---------------------------------------------------------------------------
interface FetchState {
  view: ViewState;
  data: CardData | null;
  settings: AppSettings | undefined;
  error: string | null;
  isLoading: boolean;
}

function useSelfFetch(enabled: boolean, pathname: string): FetchState {
  const [state, setState] = useState<FetchState>({
    view: 'loading',
    data: null,
    settings: undefined,
    error: null,
    isLoading: true
  });

  const lastFetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (lastFetchedRef.current === pathname) return;
    lastFetchedRef.current = pathname;

    const pathParts = pathname.substring(1).split('/').filter((p) => p);
    const p0 = pathParts[0] ?? '';
    const p1 = pathParts[1] ?? '';
    const isCardPrefixedShortCode = pathParts.length === 2 && p0 === 'card' && /^[a-zA-Z0-9]{7}$/.test(p1);
    const isShortCode = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(p0);
    // Sublink slugs are 8 chars (vs 7 for short codes) so we can disambiguate
    const isSublink = pathParts.length === 1 && /^[a-zA-Z0-9]{8}$/.test(p0);
    const isOrgScoped = pathParts.length === 2 && Boolean(p0) && Boolean(p1) && p0 !== 'card';
    const isLegacy = pathParts.length === 1 && !isShortCode && !isSublink;

    let url = '';
    if (isCardPrefixedShortCode) {
      url = `/api/public/card/short/${p1}`;
    } else if (isShortCode) {
      url = `/api/public/card/short/${p0}`;
    } else if (isSublink) {
      url = `/api/public/card/track/${p0}`;
    } else if (isOrgScoped) {
      url = `/api/public/card/${p0}/${p1}`;
    } else if (isLegacy) {
      url = `/api/public/card/${p0}`;
    } else {
      setState((s) => ({ ...s, view: '404', isLoading: false, error: 'Invalid card URL' }));
      return;
    }

    setState((s) => ({ ...s, view: 'public-loading', isLoading: true }));

    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (res.status === 404) throw new Error('404');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ data?: CardData; settings?: AppSettings; card?: CardData; locked?: boolean }>;
      })
      .then((json) => {
        const cardData = json.data || json.card || null;
        setState({
          view: json.locked ? 'locked' : cardData ? 'public-card' : '404',
          data: cardData,
          settings: json.settings,
          error: null,
          isLoading: false
        });
      })
      .catch((err: unknown) => {
        const is404 = err instanceof Error && err.message === '404';
        setState({
          view: '404',
          data: null,
          settings: undefined,
          error: is404 ? null : String(err),
          isLoading: false
        });
      });
  }, [enabled, pathname]);

  return state;
}

// ---------------------------------------------------------------------------
// PublicCardPage component
// ---------------------------------------------------------------------------
export default function PublicCardPage(props: PublicCardPageProps) {
  const { t } = useAppTranslation();
  const params = useParams();
  const location = useLocation();
  const lastFetchedPathRef = useRef<string | null>(null);

  // Local dark mode state — used when parent doesn't manage it
  const [localDarkMode, setLocalDarkMode] = useState(false);
  const darkMode = props.darkMode !== undefined ? props.darkMode : localDarkMode;
  const toggleDarkMode = props.toggleDarkMode ?? (() => setLocalDarkMode((d) => !d));

  // Determine if we are in self-contained mode (no external callbacks provided)
  const hasFetchCallbacks = Boolean(
    props.fetchCardByOrgAndSlug || props.fetchCardByShortCode || props.fetchPublicCard
  );
  const selfFetch = useSelfFetch(!hasFetchCallbacks, location.pathname);

  // Resolve effective state (external props take precedence over self-fetch)
  const view: ViewState = hasFetchCallbacks ? (props.view ?? 'loading') : selfFetch.view;
  const isPublicLoading = hasFetchCallbacks ? (props.isPublicLoading ?? false) : selfFetch.isLoading;
  const data = hasFetchCallbacks ? props.data : selfFetch.data;
  const settings = hasFetchCallbacks ? props.settings : selfFetch.settings;
  const error = hasFetchCallbacks ? props.error : selfFetch.error;

  // Apply theme whenever settings changes
  useEffect(() => {
    const variant = normalizeThemeVariantClient(settings?.theme_variant || 'vcard');
    document.body.classList.remove('theme-vcard', 'theme-minimal', 'theme-mint', 'theme-nagarro', 'theme-custom');
    document.body.classList.add(`theme-${variant}`);
    applyThemeCssVars(variant, settings?.app_theme_overrides || {});
  }, [settings]);

  // Delegate fetching to parent callbacks (when provided)
  useEffect(() => {
    if (!hasFetchCallbacks) return;

    const path = location.pathname;
    if (lastFetchedPathRef.current === path) return;

    const pathParts = path.substring(1).split('/').filter((p) => p);
    const cp0 = pathParts[0] ?? '';
    const cp1 = pathParts[1] ?? '';
    const isCardPrefixedShortCode = pathParts.length === 2 && cp0 === 'card' && /^[a-zA-Z0-9]{7}$/.test(cp1);
    const isShortCode = pathParts.length === 1 && /^[a-zA-Z0-9]{7}$/.test(cp0);
    const isOrgScoped = pathParts.length === 2 && Boolean(cp0) && Boolean(cp1) && cp0 !== 'card';
    const isLegacy = pathParts.length === 1 && !isShortCode;

    lastFetchedPathRef.current = path;

    if (isCardPrefixedShortCode && props.fetchCardByShortCode) {
      props.fetchCardByShortCode(cp1);
    } else if (isShortCode && props.fetchCardByShortCode) {
      props.fetchCardByShortCode(cp0);
    } else if (isOrgScoped && props.fetchCardByOrgAndSlug) {
      props.fetchCardByOrgAndSlug(cp0, cp1);
    } else if (isLegacy && props.fetchPublicCard) {
      props.fetchPublicCard(cp0);
    } else {
      console.error('[PublicCardPage] No valid fetch strategy for path:', path);
      lastFetchedPathRef.current = null;
    }
  }, [
    location.pathname,
    hasFetchCallbacks,
    props.fetchCardByOrgAndSlug,
    props.fetchCardByShortCode,
    props.fetchPublicCard
  ]);

  // Show loading state
  const pathParts = location.pathname.substring(1).split('/').filter((p) => p);
  const hasPublicRouteParams =
    Boolean(params.slug || params.cardSlug || params.orgSlug) || pathParts.length > 0;

  if (isPublicLoading || ((view === 'loading' || view === 'public-loading') && hasPublicRouteParams)) {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
        <div className="text-text-muted-subtle dark:text-text-muted-dark">{t('publicCard.status.loading', 'Loading...')}</div>
      </div>
    );
  }

  // Free test period over: the card owner has not booked a plan yet
  if (view === 'locked') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center p-6">
        <div className="max-w-sm text-center">
          <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 dark:bg-accent-500/15 text-accent-700 dark:text-accent-300">
            <Lock className="h-5 w-5" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">
            {t('publicCard.locked.title', 'Unbezahlte Version')}
          </h1>
          <p className="text-text-secondary dark:text-text-muted-dark">
            {t('publicCard.locked.message', 'Diese Visitenkarte ist im Moment nicht freigeschaltet, weil der Testzeitraum abgelaufen ist. Die Person hinter der Karte muss einen Plan buchen, dann ist sie unter diesem Link und QR-Code sofort wieder da.')}
          </p>
        </div>
      </div>
    );
  }

  // Show 404
  if (view === '404') {
    return (
      <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary dark:text-text-primary-dark mb-2">
            {t('publicCard.status.notFoundTitle', 'Card Not Found')}
          </h1>
          <p className="text-text-secondary dark:text-text-muted-dark">
            {error || t('publicCard.status.notFoundMessage', 'The card you are looking for does not exist.')}
          </p>
        </div>
      </div>
    );
  }

  // Show card
  if (view === 'public-card' && data && data.personal) {
    return (
      <div className={`min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-start lg:items-center p-0 lg:p-8 ${darkMode ? 'dark' : ''}`}>
        <div className="w-full max-w-md bg-card dark:bg-card-dark min-h-screen lg:min-h-0 lg:h-auto lg:rounded-page shadow-2xl overflow-hidden relative animate-in fade-in duration-500 flex flex-col">
          <div className="flex-1">
            <CardDisplay
              data={data}
              settings={settings}
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
              {...(props.showAlert !== undefined ? { showAlert: props.showAlert } : {})}
            />
          </div>
        </div>
      </div>
    );
  }

  // Fallback (safety net)
  return (
    <div className="min-h-screen bg-main dark:bg-main-dark bg-main-texture flex justify-center items-center">
      <div className="text-text-muted-subtle dark:text-text-muted-dark">{t('publicCard.status.loading', 'Loading...')}</div>
    </div>
  );
}
